// Component: UserDemographics
// Purpose: Display user demographic statistics and metrics in the admin dashboard

import React from 'react';
import { 
  Users, UserCheck, Crown, Shield, Calendar, 
  RefreshCw, Gift, Building2, UserPlus, TrendingUp, Clock, Trash2
} from 'lucide-react';

// Helper function to format numbers with commas
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

export const UserDemographics = ({
  displayUsers,
  userData,
  newPremiumUsers,
  trialPremiumUsers,
  trialMigrationUsers,
  activeUsers,
  enterpriseUsers,
  newCustomers,
  basicUsers,
  formerSubscribers,
  formerTrialUsers,
  freeUsers,
  nonRenewingUsers,
  isPayingPremiumUser,
  isTrialPremiumUser,
  isNonRenewingPayingUser,
  isFreeUser,
  getTrialEndDate
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">User Demographics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-8 gap-4">
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">New Premium Users Today (CST)</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(newPremiumUsers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Paying Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber((displayUsers.filter(user => 
                  (isPayingPremiumUser(user) || isNonRenewingPayingUser(user)) && !isFreeUser(user)
                ).length + trialMigrationUsers.length) || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Crown className="h-5 w-5 text-purple-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Premium Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(displayUsers.filter(user => 
                  (isPayingPremiumUser(user) || isTrialPremiumUser(user) || isNonRenewingPayingUser(user)) && !isFreeUser(user)
                ).length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trials Ending Today (CST)</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(trialPremiumUsers.filter(user => {
                  const trialEnd = getTrialEndDate(user);
                  if (!trialEnd) return false;
                  const today = new Date();
                  const todayCST = new Date(today.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                  todayCST.setHours(0, 0, 0, 0);
                  const trialEndCST = new Date(trialEnd.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                  trialEndCST.setHours(0, 0, 0, 0);
                  return todayCST.getTime() === trialEndCST.getTime();
                }).length || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Trials Ending by Day of Week */}
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4 col-span-2">
          <div className="flex items-center mb-3">
            <Calendar className="h-5 w-5 text-purple-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trials Ending This Week (CST)</p>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2 text-xs">
            {(() => {
              // Get trials ending by day of week
              const trialsByDay = {
                'Sun': 0, 'Mon': 0, 'Tue': 0, 'Wed': 0, 'Thu': 0, 'Fri': 0, 'Sat': 0
              };
              
              const now = new Date();
              const oneWeekFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));
              
              trialPremiumUsers.forEach(user => {
                const trialEnd = getTrialEndDate(user);
                if (trialEnd && trialEnd >= now && trialEnd <= oneWeekFromNow) {
                  const dayOfWeek = trialEnd.toLocaleDateString("en-US", {
                    timeZone: "America/Chicago",
                    weekday: 'short'
                  });
                  if (trialsByDay.hasOwnProperty(dayOfWeek)) {
                    trialsByDay[dayOfWeek]++;
                  }
                }
              });
              
              return Object.entries(trialsByDay).map(([day, count]) => (
                <div key={day} className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{day}</div>
                  <div className={`text-lg font-semibold ${count > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                    {count}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Currently Active</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(activeUsers.length || 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatNumber(userData.summary?.totalUsers || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <UserPlus className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">New Basic Users (Today CST)</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(newCustomers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <UserCheck className="h-5 w-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Basic Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatNumber(basicUsers.length || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <RefreshCw className="h-5 w-5 text-orange-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Former Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(formerSubscribers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-yellow-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Former Trial Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(formerTrialUsers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Trash2 className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deleted Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatNumber(userData.summary?.totalDeletedUsers || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Building2 className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Enterprise Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(enterpriseUsers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Gift className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Free Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatNumber(freeUsers.length || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-purple-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Annual Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(displayUsers.filter(user => 
                  user.planInterval === 'annual' && 
                  (isPayingPremiumUser(user) || isNonRenewingPayingUser(user)) && 
                  !isFreeUser(user)
                ).length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <RefreshCw className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Not Renewing Premium</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(nonRenewingUsers.length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">$22 Paying Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(displayUsers.filter(user => 
                  (isPayingPremiumUser(user) || isNonRenewingPayingUser(user)) && 
                  !isFreeUser(user) && 
                  user.monthlyPrice === 22
                ).length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-purple-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">$34 Paying Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(displayUsers.filter(user => 
                  (isPayingPremiumUser(user) || isNonRenewingPayingUser(user)) && 
                  !isFreeUser(user) && 
                  user.monthlyPrice === 34
                ).length || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total MRR</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {(() => {
                  // Calculate MRR from current paying users
                  const now = new Date();
                  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                  const isMigratedTrialEligibleThisMonth = (u) => {
                    if (u?.migratedBackToLegacy === true && (u?.subscriptionStatus || '').toLowerCase() === 'trialing') {
                      const t = u.trialEnd?.toDate ? u.trialEnd.toDate() : (u.trialEnd ? new Date(u.trialEnd) : null);
                      const start = u.subscriptionPeriodStart?.toDate ? u.subscriptionPeriodStart.toDate() : (u.subscriptionPeriodStart ? new Date(u.subscriptionPeriodStart) : null);
                      const trialEnded = t && isFinite(t.getTime()) && t.getTime() <= now.getTime();
                      const originatedThisMonth = start && start >= monthStart && start < nextMonthStart;
                      return trialEnded || originatedThisMonth;
                    }
                    return false;
                  };
                  const eligible = displayUsers.filter(u => (
                    (isPayingPremiumUser(u) || isNonRenewingPayingUser(u) || isMigratedTrialEligibleThisMonth(u)) && !isFreeUser(u)
                  ));
                  const users22Count = eligible.filter(u => u.monthlyPrice === 22).length;
                  const users34Count = eligible.filter(u => u.monthlyPrice === 34).length;
                  const totalMRR = (users22Count * 22) + (users34Count * 34);
                  return `$${formatNumber(totalMRR)}`;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


